import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { api, type Entry } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { getMoodByText, moodOptions, getSentimentDisplay } from '@/types/index';
import MoodSelector from '@/components/MoodSelector';
import TagEditor from '@/components/TagEditor';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { ArrowLeft, Edit2, Trash2, Save, X, Loader2, Copy, Check, Share2, ImagePlus } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function EntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [entry, setEntry] = useState<Entry | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editMood, setEditMood] = useState<string | null>(null);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [editSentimentScore, setEditSentimentScore] = useState<number | null>(null);
  const { isPremium } = useSubscription();

  useEffect(() => {
    if (!id) return;
    api.getEntry(id)
      .then(e => {
        setEntry(e);
        setEditContent(e.content);
        const m = e.mood ? getMoodByText(e.mood) : undefined;
        setEditMood(m?.id ?? null);
        setEditTags(e.tags || []);
      })
      .catch(() => toast({ title: 'エントリの読み込みに失敗しました', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleSave = async () => {
    if (!id || !entry) return;
    setSaving(true);
    try {
      const moodData = editMood ? moodOptions.find(m => m.id === editMood) : undefined;
      const moodStr = moodData ? `${moodData.emoji} ${moodData.label}` : undefined;
      const newCreatedAt = editDate && editTime
        ? new Date(`${editDate}T${editTime}:00`).toISOString()
        : undefined;
      const updated = await api.updateEntry(id, {
        content: editContent,
        mood: moodStr,
        tags: editTags,
        sentiment_score: editSentimentScore ?? undefined,
        ...(newCreatedAt && { date: editDate, created_at: newCreatedAt }),
      });
      setEntry(updated);
      setEditing(false);
      toast({ title: '保存しました' });
    } catch {
      toast({ title: '保存に失敗しました', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setEditing(true);
    const m = entry?.mood ? getMoodByText(entry.mood) : undefined;
    setEditMood(m?.id ?? null);
    setEditTags(entry?.tags || []);
    setEditContent(entry?.content || '');
    setEditSentimentScore(entry?.sentiment_score ?? null);
    // 日付・時刻を初期化
    const src = entry?.created_at || entry?.date || '';
    if (src) {
      const d = parseISO(src);
      setEditDate(format(d, 'yyyy-MM-dd'));
      setEditTime(format(d, 'HH:mm'));
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditContent(entry?.content || '');
    const m = entry?.mood ? getMoodByText(entry.mood) : undefined;
    setEditMood(m?.id ?? null);
    setEditTags(entry?.tags || []);
    setEditSentimentScore(entry?.sentiment_score ?? null);
    const src = entry?.created_at || entry?.date || '';
    if (src) {
      const d = parseISO(src);
      setEditDate(format(d, 'yyyy-MM-dd'));
      setEditTime(format(d, 'HH:mm'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.deleteEntry(id);
      toast({ title: '削除しました' });
      navigate('/timeline');
    } catch {
      toast({ title: '削除に失敗しました', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const MAX_IMAGES = 2;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!id || !entry || files.length === 0) return;
    const currentCount = (entry.image_urls || []).length;
    const allowed = files.slice(0, MAX_IMAGES - currentCount);
    if (allowed.length === 0) {
      toast({ title: `画像は${MAX_IMAGES}枚までです`, variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setImageUploading(true);
    try {
      const urls = [...(entry.image_urls || [])];
      for (const file of allowed) {
        const url = await api.uploadEntryImage(id, file);
        urls.push(url);
      }
      const updated = await api.updateEntry(id, { image_urls: urls });
      setEntry(updated);
      toast({ title: '画像を追加しました' });
    } catch (err) {
      console.error('[EntryDetail] image upload failed:', err);
      toast({
        title: '画像のアップロードに失敗しました',
        description: err instanceof Error ? err.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  const handleImageDelete = async (url: string) => {
    if (!id || !entry) return;
    try {
      await api.deleteEntryImage(id, url);
      const urls = (entry.image_urls || []).filter(u => u !== url);
      const updated = await api.updateEntry(id, { image_urls: urls });
      setEntry(updated);
      toast({ title: '画像を削除しました' });
    } catch {
      toast({ title: '画像の削除に失敗しました', variant: 'destructive' });
    }
  };

  const handleTagClick = (tag: string) => {
    navigate(`/timeline?tag=${encodeURIComponent(tag)}`);
  };

  const handleCopyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(entry?.content || '');
      setCopied(true);
      toast({ title: 'テキストをコピーしました' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'コピーに失敗しました', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">エントリが見つかりません</p>
        <Button variant="link" onClick={() => navigate('/timeline')}>
          タイムラインに戻る
        </Button>
      </div>
    );
  }

  const mood = entry.mood ? getMoodByText(entry.mood) : undefined;
  // Prefer created_at for time display (entry.date is date-only, causes 00:00)
  const dateStr = entry.created_at || entry.date;

  // Strip YAML frontmatter from content (legacy entries)
  const stripFrontmatter = (text: string) => {
    return text.replace(/^---[\s\S]*?---\s*/, '').replace(/^#\s+.*\n+>\s+.*\n+##\s+Transcript\s*\n/, '').trim();
  };
  const cleanContent = stripFrontmatter(entry.content || '');

  // Share helpers — short-form: body text, long-form: title + body
  const appUrl = 'https://hitorigoto.app';
  const tag = '#Hitorigoto';

  const buildShortText = () => {
    const icon = mood?.emoji || '📝';
    const maxLen = 280 - tag.length - 23 - icon.length - 4; // URLはt.co短縮で23文字固定
    const body = cleanContent.length > maxLen ? cleanContent.substring(0, maxLen - 1) + '…' : cleanContent;
    return `${icon} ${body}\n${tag}\n${appUrl}`;
  };

  const shareToX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(buildShortText())}`, '_blank', 'noopener,noreferrer');
  };
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(buildShortText())}`, '_blank', 'noopener,noreferrer');
  };
  const shareToNote = async () => {
    const noteTitle = entry.title || 'ひとりごと';
    try {
      await navigator.clipboard.writeText(cleanContent);
      toast({ title: `本文をコピーしました`, description: `タイトルに「${noteTitle}」を入力して本文を貼り付けてください` });
    } catch {
      toast({ title: 'コピーに失敗しました', variant: 'destructive' });
    }
    const hashtags = encodeURIComponent('Hitorigoto');
    const url = encodeURIComponent(appUrl);
    window.open(`https://note.com/intent/post?url=${url}&hashtags=${hashtags}`, '_blank', 'noopener,noreferrer');
  };
  const shareToBluesky = () => {
    window.open(`https://bsky.app/intent/compose?text=${encodeURIComponent(buildShortText())}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      className="space-y-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          戻る
        </Button>
        <div className="flex gap-1.5">
          {editing ? (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={cancelEditing}>
                <X className="h-4 w-4" />
              </Button>
              <Button size="icon" className="h-8 w-8" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </Button>
            </>
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={shareToX} className="gap-2">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                    X (Twitter)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToFacebook} className="gap-2">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                    Facebook
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToBluesky} className="gap-2">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.785 2.627 3.59 3.501 6.158 3.186-3.344.516-6.274 1.776-3.655 6.108C5.862 23.644 10.637 20.2 12 15.99c1.363 4.21 6.093 7.538 8.873 3.551 2.619-4.332-.311-5.592-3.655-6.108 2.568.315 5.373-.56 6.158-3.186C23.622 9.418 24 4.458 24 3.768c0-.69-.139-1.861-.902-2.203-.659-.3-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z" /></svg>
                    Bluesky
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToNote} className="gap-2">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm3 3h8v2H8V8zm0 4h8v2H8v-2z" /></svg>
                    note
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={startEditing}>
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCopyMarkdown}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Title + Date + Mood header */}
      <div className="space-y-2">
        <div className="flex items-start gap-3">
          {mood && <span className="text-3xl mt-0.5">{mood.emoji}</span>}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold leading-tight">{entry.title || 'エントリ'}</h1>
            {editing ? (
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  type="date"
                  value={editDate}
                  onChange={e => setEditDate(e.target.value)}
                  className="px-2 py-1 rounded-md border bg-background text-sm"
                />
                <input
                  type="time"
                  value={editTime}
                  onChange={e => setEditTime(e.target.value)}
                  className="px-2 py-1 rounded-md border bg-background text-sm"
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground">
                {dateStr && (
                  <span>{format(parseISO(dateStr), 'yyyy年M月d日 (E) HH:mm', { locale: ja })}</span>
                )}
                {mood && <span>{mood.label}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        {entry.summary && (
          <p className="text-sm text-muted-foreground border-l-2 border-primary/30 pl-3 italic">
            {entry.summary}
          </p>
        )}

        {/* AI Comment */}
        {entry.ai_comment && (
          <p className="text-xs text-muted-foreground/80 flex items-center gap-1.5">
            <span>💭</span>
            <span>{entry.ai_comment}</span>
          </p>
        )}

        {/* Character count & reading time — pill badges */}
        {cleanContent && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium">
              {cleanContent.length.toLocaleString()}文字
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium">
              約{Math.max(1, Math.round(cleanContent.length / 500))}分で読了
            </span>
          </div>
        )}

        {/* Tags (clickable → navigate to timeline with tag filter) + Sentiment */}
        {((entry.tags && entry.tags.length > 0) || entry.sentiment_score !== undefined) && (
          <div className="flex items-center gap-2 flex-wrap">
            {entry.tags?.map(tag => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs cursor-pointer hover:bg-primary/20 transition-colors"
                onClick={() => handleTagClick(tag)}
              >
                {tag}
              </Badge>
            ))}
            {entry.sentiment_score !== undefined && (
              <Badge variant={entry.sentiment_score > 0.3 ? "default" : entry.sentiment_score < -0.3 ? "destructive" : "secondary"} className="text-xs">
                感情 {entry.sentiment_score > 0 ? '+' : ''}{(entry.sentiment_score * 100).toFixed(0)}%
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Entry content */}
      <Card className="rounded-xl shadow-sm">
        <CardContent className="pt-5">
          {editing ? (
            <Textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="min-h-[300px] text-sm"
            />
          ) : (
            <div className="whitespace-pre-wrap leading-relaxed text-sm">
              {cleanContent}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image gallery */}
      {(entry.image_urls && entry.image_urls.length > 0) && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {entry.image_urls.map((url, i) => (
            <div key={i} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border cursor-pointer" onClick={() => setPreviewImage(url)}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              {editing && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleImageDelete(url); }}
                  className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image upload button (editing + premium only, max 2) */}
      {editing && isPremium && (entry.image_urls || []).length < MAX_IMAGES && (
        <label className="inline-flex cursor-pointer">
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleImageUpload} />
          <Button variant="outline" size="sm" className="gap-2 pointer-events-none" disabled={imageUploading}>
            {imageUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            画像を追加
          </Button>
        </label>
      )}

      {/* Mood / Sentiment / Tags editing */}
      {editing && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="rounded-xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">気分</CardTitle>
              </CardHeader>
              <CardContent>
                <MoodSelector selected={editMood} onChange={setEditMood} />
              </CardContent>
            </Card>
            <Card className="rounded-xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">感情指標</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const score = editSentimentScore ?? 0;
                  const display = getSentimentDisplay(score);
                  const pct = Math.round(score * 100);
                  return (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className={display.color}>{display.label}</span>
                        <span className="font-mono text-muted-foreground">
                          {pct > 0 ? '+' : ''}{pct}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min={-100}
                        max={100}
                        step={10}
                        value={Math.round(score * 100)}
                        onChange={e => setEditSentimentScore(Number(e.target.value) / 100)}
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-primary bg-muted"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>つらい</span>
                        <span>穏やか</span>
                        <span>前向き</span>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
          <Card className="rounded-xl shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">タグ</CardTitle>
            </CardHeader>
            <CardContent>
              <TagEditor selectedTags={editTags} onTagsChange={setEditTags} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image preview modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>画像プレビュー</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <img src={previewImage} alt="" className="w-full h-auto max-h-[80vh] object-contain rounded" />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>エントリを削除</DialogTitle>
            <DialogDescription>
              このエントリを削除しますか？この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              キャンセル
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </motion.div>
  );
}
